import { createFileRoute, Link } from '@tanstack/react-router'
import { DocPage } from '../components/docs/DocPage'
import { Section, P, DefList, C, Pre, H3 } from '../components/docs/prose'

export const Route = createFileRoute('/docs/reference')({ component: Reference })

const SECTIONS = [
  { id: 'api', title: 'The API' },
  { id: 'tokens', title: 'Personal access tokens' },
  { id: 'endpoints', title: 'Endpoint groups' },
  { id: 'exports', title: 'Exports' },
  { id: 'imports', title: 'CSV import' },
]

function Reference() {
  return (
    <DocPage
      path="/docs/reference"
      title="API and exports"
      lede={
        <>
          Everything the app does, it does over the same REST API you can use.
          The full generated schema lives behind{' '}
          <Link to="/api-docs" style={{ color: 'var(--accent-text)' }}>API docs</Link>{' '}
          once you are signed in; this page is the orientation.
        </>
      }
      sections={SECTIONS}
    >
      <Section id="api" title="The API">
        <P>
          Base path is <C>/api/v1</C>. Everything is JSON. Two things sit
          outside it: <C>GET /health</C>, and the generated schema at{' '}
          <C>/swagger</C>.
        </P>
        <P>
          There are two ways to authenticate, both as a bearer token. A JWT
          from signing in, which lasts an hour and is refreshed by the app; or
          a personal access token, which is what you want for scripts.
        </P>
        <P>
          Data isolation is enforced in Postgres by row-level security, not in
          the handlers. Every authenticated request sets a tenant context for
          the duration of the transaction, and the policies decide what rows
          exist. A query that forgot to filter by household returns nothing
          rather than someone else&rsquo;s data.
        </P>
      </Section>

      <Section id="tokens" title="Personal access tokens">
        <P>
          Create them on the{' '}
          <Link to="/api-tokens" style={{ color: 'var(--accent-text)' }}>API tokens</Link>{' '}
          page. A token looks like this:
        </P>
        <Pre>{`fw_3f9a2c8e41b07d65a3e1c94f8802db7a5c6e19f34ab8d0725e6f1c93a4b8072d

Authorization: Bearer fw_3f9a2c8e…`}</Pre>
        <DefList
          items={[
            ['Shown once', 'The full token appears only in the response that created it. FolioWorth stores a SHA-256 hash and an eleven-character prefix, so it can identify the token in a list without being able to reproduce it.'],
            ['Expiry is in days', 'Set it when you create the token. Omit it and the token never expires, which is rarely what you want for anything running on a server.'],
            ['Revocation is a delete', 'Revoking removes the row. There is no deactivated state to resurrect, and the token stops working on the next request.'],
            ['Scopes are not enforced yet', 'A token can be labelled read or write, and that label is stored and returned, but nothing checks it. Treat every token as full access to your household until that changes.'],
          ]}
        />
      </Section>

      <Section id="endpoints" title="Endpoint groups">
        <P>
          The shape of the surface, so you know where to look in the schema:
        </P>
        <DefList
          items={[
            [<C>/accounts</C>, 'Accounts, balances, and the snapshot-based net worth.'],
            [<C>/net-worth</C>, 'The ledger-derived net worth series.'],
            [<C>/transactions</C>, 'Transaction list and detail.'],
            [<C>/bills</C>, 'Payees, bill instances, the matrix behind the payment grid, suggestions, and reconciliation.'],
            [<C>/flow</C>, 'Nodes, edges, account drill-down, and saved views.'],
            [<C>/investments</C> , 'Lots, ACB positions and capital-gains calculation.'],
            [<C>/portfolio</C>, 'Holdings and allocation.'],
            [<C>/budgets</C>, 'Budgets, and /goals for goals.'],
            [<C>/categories</C>, 'Categories, groups, and /categorization-rules for the rules that assign them.'],
            [<C>/recurring-transactions</C>, 'Recurring entries, the upcoming lookahead, and pattern detection.'],
            [<C>/plaid</C>, 'Link tokens, connections, and sync.'],
            [<C>/ai</C>, 'Insights, refresh, and AI settings.'],
            [<C>/households</C>, 'Members and invitations. /profile for the signed-in user.'],
            [<C>/reports</C>, 'Aggregations, and /dashboard for saved layouts.'],
            [<C>/import</C> , 'CSV preview and import. /export for CSV out.'],
          ]}
        />
      </Section>

      <Section id="exports" title="Exports">
        <P>
          <C>GET /api/v1/export/csv</C> returns transactions as CSV, optionally
          filtered by account and date range, up to ten thousand rows per
          request. The columns are date, account, type, amount, category,
          description, payee or payer, and reference.
        </P>
        <P>
          That is the whole of it today — transactions only. Accounts, bills,
          budgets, goals, holdings and the raw ledger have no CSV export, so
          anything else has to come out through the API. Given that the ledger
          is the authoritative record, a ledger export is the obvious gap.
        </P>
      </Section>

      <Section id="imports" title="CSV import">
        <P>
          Import is a two-step: post the file to the preview endpoint, which
          returns the headers, the first five rows and a total count; then post
          a column mapping with the rows.
        </P>
        <P>
          Columns you can map to: <C>date</C>, <C>amount</C>,{' '}
          <C>description</C>, <C>category</C>, <C>payee_payer</C>,{' '}
          <C>reference_number</C>, <C>transaction_type</C>. Amounts tolerate
          dollar signs and thousands separators. A negative amount is treated
          as a withdrawal and anything else as a deposit.
        </P>
        <H3>Dates and duplicates</H3>
        <P>
          Date parsing falls through several common layouts, including{' '}
          <C>2006-01-02</C>, <C>01/02/2006</C>, <C>Jan 2, 2006</C> and ISO
          timestamps. Note that <C>01/02/2006</C> is read as month-first, so
          check a few rows if your bank exports day-first.
        </P>
        <P>
          Duplicate detection, when enabled, is exact: same account, same date,
          same amount. It will not catch a bank that re-exports the same
          transaction with a shifted posting date, so import ranges you have
          not imported before.
        </P>
      </Section>
    </DocPage>
  )
}
